import React, { forwardRef, memo, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { LayoutChangeEvent, Platform, StyleSheet, View, ViewStyle } from 'react-native';
import { Gesture, GestureDetector } from 'react-native-gesture-handler';
import Animated, {
    SharedValue,
    runOnJS,
    useAnimatedStyle,
    useDerivedValue,
    useFrameCallback,
    useSharedValue,
    withDecay,
} from 'react-native-reanimated';

import { Box } from '@/components/lib/components';
import { common } from '@/styles/styles';

export type AutoScrollingListLoopProps<T> = {
    data: readonly T[];
    renderItem: (args: { item: T; index: number }) => React.ReactElement;
    keyExtractor?: (item: T, index: number) => string | number;
    speed?: number;
    spacing?: number;
    style?: ViewStyle;
    frameRate?: number;
    position?: SharedValue<number>;
    autostart?: boolean;
    bufferScreens?: number;
    initialIndex?: number;
};

export interface AutoScrollingListLoopRef {
    start: () => void;
    stop: () => void;
    isActive: boolean;
}

export const AUTO_SCROLL_LIST_ENABLED = Platform.OS === 'ios';

/**
 * Wraps a numeric value into the [0, range) interval.
 * Handles negative and overflow values by performing a modulo operation
 * in a way that always returns a non-negative remainder.
 * @param value - The input number (can be negative or > range)
 * @param range - The modulus range; must be > 0
 * @returns A number in [0, range)
 */
const wrapToRange = (v: number, row: number) => {
    'worklet';
    if (row === 0) {
        return 0;
    }
    return ((v % row) + row) % row;
};

/**
 * AnimatedChild wraps an individual list item and applies a circular translation
 * that allows items to slide leftward and reappear at the right edge
 * seamlessly across the total cycle width.
 *
 * Props:
 * - shift: SharedValue<number> representing the scroll offset.
 * - left: number representing this item's base X position in the cycle (the initial offset from the first render)
 * - width: number of the item's rendered width (used to calculate the wrap point).
 * - totalWidth: total width of the full cycle (all clones concatenated).
 *
 * Behavior:
 * - If totalWidth is zero (not yet measured), we simply position the view absolutely at `left`.
 * - Otherwise, we compute the wrapped translateX via modulo arithmetic:
 *   1. Compute raw position: left + width + shift.value
 *   2. Wrap into [0, totalWidth) via wrapToRange()
 *   3. Subtract (left + width) to get the relative offset
 * - This ensures items slide leftward and reappear at the right edge, after another full cycle width is finished
 */
const AnimatedChild = React.memo(
    ({
        shift,
        left,
        width,
        totalWidth,
        children,
    }: React.PropsWithChildren<{
        shift: SharedValue<number>;
        left: number;
        width: number;
        totalWidth: number;
    }>) => {
        const style = useAnimatedStyle(() => {
            'worklet';
            if (totalWidth === 0) {
                return { position: 'absolute', left };
            }
            const translateX = wrapToRange(left + width + shift.value, totalWidth) - (left + width);
            return { position: 'absolute', left, transform: [{ translateX }] };
        });
        return <Animated.View style={style}>{children}</Animated.View>;
    }
);

const AutoScrollingListLoopInner = <T,>(
    {
        data,
        renderItem,
        keyExtractor,
        speed = 1,
        spacing = 0,
        style,
        frameRate,
        position,
        autostart = true,
        bufferScreens = 2,
        initialIndex = 0,
    }: AutoScrollingListLoopProps<T>,
    ref: React.Ref<AutoScrollingListLoopRef>
) => {
    const itemCount = data.length;

    /**
     * Compute a new ordering of indices so that the sequence starts at initialIndex-1
     * e.g., for initialIndex=4 in a 7-item list, start at index 3: [3,4,5,6,0,1,2]
     * if we have 2 items, we just return [0, 1] since we don't need to center around any item (keep original order)
     */
    const order = useMemo(() => {
        if (itemCount === 0) {
            return [] as number[];
        }
        if (itemCount === 2) {
            // Special case for 2 items, no new order needed
            return [0, 1] as number[];
        }

        const start = (((initialIndex - 1) % itemCount) + itemCount) % itemCount;
        const arr: number[] = [];
        for (let k = 0; k < itemCount; k++) {
            arr.push((start + k) % itemCount);
        }
        return arr;
    }, [initialIndex, itemCount]);

    // Rendered elements in the new order, "centered" around initialIndex
    const elements = useMemo(
        () => order.map(idx => renderItem({ item: data[idx], index: idx })),
        [order, data, renderItem]
    );

    const widthsRef = useRef<number[]>([]);
    const [allMeasured, setAllMeasured] = useState(false);

    // initialize widthsRef with zeros for each item
    useEffect(() => {
        widthsRef.current = Array(itemCount).fill(0);
        setAllMeasured(false);
    }, [itemCount]);

    // Called by onLayout of each Box to record width
    const reportWidth = useCallback(
        (i: number, w: number) => {
            widthsRef.current[i] = w;
            if (!allMeasured && widthsRef.current.every(v => v > 0)) {
                setAllMeasured(true);
            }
        },
        [allMeasured]
    );

    // Compute offsets of each item and total content width
    const { itemOffsets, totalWidth } = useMemo(() => {
        if (!allMeasured) {
            return { itemOffsets: [], totalWidth: 0 };
        }
        const offs: number[] = [];
        let acc = 0;

        widthsRef.current.forEach(w => {
            offs.push(acc);
            acc += w + spacing;
        });

        return { itemOffsets: offs, totalWidth: acc };
    }, [allMeasured, spacing]);

    const [parentWidth, setParentWidth] = useState(0);
    const [parentHeight, setParentHeight] = useState(0);
    const onParentLayout = useCallback(
        (e: LayoutChangeEvent) => {
            const { width, height } = e.nativeEvent.layout;
            if (width !== parentWidth) {
                setParentWidth(width);
            }
            if (height !== parentHeight) {
                setParentHeight(height);
            }
        },
        [parentWidth, parentHeight]
    );

    const cloneGroups = useMemo(() => {
        if (!allMeasured || !parentWidth) {
            return 1;
        }
        // if totalWidth is bigger than the required window, we only need one group
        if (totalWidth >= parentWidth * bufferScreens) {
            return 1;
        }

        // compute how many clone groups we need to fill the screen
        const missing = parentWidth * bufferScreens - totalWidth;
        // each group adds totalWidth to the cycle, so we need enough groups to cover the missing width
        return 1 + Math.ceil(missing / totalWidth);
    }, [allMeasured, parentWidth, totalWidth, bufferScreens]);

    // Animation state
    const anim = useSharedValue(0);
    const frameInterval = frameRate ? 1000 / frameRate : null;
    const frameCb = useFrameCallback(frame => {
        if (!frame.timeSincePreviousFrame) {
            return;
        }
        const dt = frameInterval ? frame.timeSincePreviousFrame / frameInterval : 1;
        anim.value += speed * dt;
    }, autostart);

    const shift = useDerivedValue(() => (totalWidth === 0 ? 0 : -wrapToRange(anim.value, totalWidth)), [totalWidth]);

    useDerivedValue(() => {
        if (position) {
            position.value = anim.value;
        }
    });

    // API interface to start/stop the animation
    const start = useCallback(() => frameCb.setActive(true), [frameCb]);
    const stop = useCallback(() => frameCb.setActive(false), [frameCb]);
    useImperativeHandle(ref, () => ({ start, stop, isActive: frameCb.isActive }));

    const pan = useMemo(
        () =>
            Gesture.Pan()
                .onBegin(() => runOnJS(stop)())
                .onChange(e => (anim.value += -e.changeX))
                .onFinalize(
                    e => (anim.value = withDecay({ velocity: -e.velocityX }, done => done && runOnJS(start)()))
                ),
        [anim, start, stop]
    );

    // scroll offset logic
    // we need to keep track of the scroll position in JS for the virtualization logic
    // we wouldn't want to use the animated value directly,and call `setScrollPos` on every frame
    // because that would cause a lot of re-renders and performance issues
    // to avoid this, we keep track only of the last reported integer position
    // and save the scroll position only when it is a whole integer number

    const lastReportedInteger = useSharedValue<number>(-1);
    const [scrollPos, setScrollPos] = useState(0);
    useDerivedValue(() => {
        const x = wrapToRange(anim.value, totalWidth);

        // floor to integer to detect "significant" movement
        const xi = Math.floor(x);

        // if the integer bucket changed, update JS
        if (xi !== lastReportedInteger.value) {
            lastReportedInteger.value = xi;
            runOnJS(setScrollPos)(x);
        }
    }, [anim.value, totalWidth]);

    const staticViewStyle = useMemo(() => {
        return [common.row, { opacity: allMeasured && totalWidth <= parentWidth ? 1 : 0 }];
    }, [allMeasured, totalWidth, parentWidth]);

    // If no items or all widths are not measured, render a static layout
    if (!allMeasured || !parentWidth || totalWidth <= parentWidth) {
        return (
            <View style={[style, staticViewStyle]} onLayout={onParentLayout} pointerEvents="box-none">
                {elements.map((el, i) => (
                    <Box
                        key={keyExtractor ? keyExtractor(data[i], i) : i}
                        onLayout={e => reportWidth(i, e.nativeEvent.layout.width)}
                        style={i < itemCount - 1 ? { marginRight: spacing } : undefined}
                    >
                        {el}
                    </Box>
                ))}
            </View>
        );
    }

    return (
        <View style={[style, { height: parentHeight }]} onLayout={onParentLayout} pointerEvents="box-none">
            <GestureDetector gesture={pan}>
                <Animated.View style={[StyleSheet.absoluteFill, common.row]} pointerEvents="box-none">
                    {Array.from({ length: cloneGroups }).map((_, g) =>
                        elements.map((el, i) => {
                            // Compute the item's absolute left position within the infinite cycle:
                            // - itemOffsets[i]: offset within one group
                            // - g * totalWidth: offset of the clone group
                            const left = itemOffsets[i] + g * totalWidth;

                            // Determine the item's right edge (left + its width):
                            const right = left + widthsRef.current[i];

                            // Use one viewport width as buffer so we render items just off-screen
                            const buffer = parentWidth;

                            // Define the primary visible window [windowStart, windowEnd]:
                            // - windowStart: start of window minus buffer
                            // - windowEnd: end of window plus buffer
                            const windowStart = scrollPos - buffer;
                            let windowEnd = scrollPos + parentWidth + buffer;

                            // If windowEnd extends beyond totalWidth, compute wrappedWindowEnd to capture
                            // items that wrap around to the start of the content
                            const wrappedWindowEnd = windowEnd - totalWidth;

                            // Check if item overlaps the primary window
                            const intersectsMainWindow = right >= windowStart && left <= windowEnd;

                            // If the window wraps past the end, also allow items from the
                            // beginning of the cycle to appear
                            const intersectsWrappedWindow = windowEnd > totalWidth && left <= wrappedWindowEnd;

                            // Cull items that are neither in the primary nor wrapped window
                            if (!intersectsMainWindow && !intersectsWrappedWindow) {
                                return null;
                            }
                            const cycleWidth = totalWidth * cloneGroups;

                            return (
                                <AnimatedChild
                                    key={`${g}-${i}`}
                                    shift={shift}
                                    left={left}
                                    width={widthsRef.current[i]}
                                    totalWidth={cycleWidth}
                                >
                                    {el}
                                </AnimatedChild>
                            );
                        })
                    )}
                </Animated.View>
            </GestureDetector>
        </View>
    );
};

/**
 * AutoScrollingListLoop is an infinite looping scrolling list that:
 * 1. Measures each child's width and computes total content size.
 * 2. Duplicates the sequence enough times (cloneGroups) to prevent gaps.
 * 3. Uses a Reanimated frame callback driving a shared `anim` value for continuous scroll.
 * 4. Wraps each item in AnimatedChild, applying a circular translateX based on the full cycle width.
 * 5. Captures pan gestures via GestureDetector to pause, drag, and fling (with `withDecay`).
 * 6. Manually "virtualizes" by culling items outside a buffered viewport to minimize mounts.
 *
 * Phases:
 * - Measurement: Render static children to record widths via onLayout.
 * - Static Center: If content fits, center without scrolling.
 * - Infinite Scroll: Render visible items across clone groups and animate them in a loop.
 *
 * Notes:
 * - the list creates its own array of items based on the `data` prop and the `renderItem` function
 * - this list is created in such a way that "initialIndex" is the second item in the list
 *   and all other items wrapped around it accordingly
 * - this way we have a somewhat "centered" initial index, without triggering a re-render (which caused performance issues)
 */
export const AutoScrollingListLoop = memo(forwardRef(AutoScrollingListLoopInner)) as <T>(
    props: AutoScrollingListLoopProps<T> & { ref?: React.Ref<AutoScrollingListLoopRef> }
) => React.ReactElement;
